import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography, Stack, Chip } from '@mui/material';
import {
  isAllowedTenantApiHost,
  isAllowedTenantReturnHost,
  isMetaEmbeddedSignupMessageOrigin,
  parseWaEmbeddedSignupFinishPayload,
} from '../config/embeddedSignup';

type Status = 'preparing' | 'waiting' | 'finishing' | 'success' | 'error';

interface DebugEntry {
  ts: string;
  origin: string;
  fromMeta: boolean;
  isWaSignup: boolean;
  preview: string;
}

const POPUP_NAME = 'pooh_meta_wa_embedded';
const MESSAGE_WAIT_MS = 15 * 60 * 1000;
const POPUP_CLOSED_POLL_MS = 1500;

interface LauncherParams {
  metaUrl: string;
  apiFinishUrl: string;
  state: string;
  returnUrl: string;
  tenant: string;
}

function readLauncherParams(sp: URLSearchParams): { params: LauncherParams | null; error?: string } {
  const metaUrl = sp.get('metaUrl') || '';
  const apiFinishUrl = sp.get('apiFinishUrl') || '';
  const state = sp.get('state') || '';
  const returnUrl = sp.get('returnUrl') || '';
  const tenant = sp.get('tenant') || '';

  if (!metaUrl || !apiFinishUrl || !state || !returnUrl) {
    return { params: null, error: 'Parâmetros obrigatórios em falta no launcher (metaUrl, apiFinishUrl, state, returnUrl).' };
  }

  try {
    const meta = new URL(metaUrl);
    if (!meta.hostname.endsWith('facebook.com')) {
      return { params: null, error: 'metaUrl não é uma URL de business.facebook.com.' };
    }
  } catch {
    return { params: null, error: 'metaUrl inválida.' };
  }

  try {
    const api = new URL(apiFinishUrl);
    if (!isAllowedTenantApiHost(api.hostname)) {
      return { params: null, error: `apiFinishUrl não permitida (${api.hostname}). Verifique o domínio do tenant.` };
    }
  } catch {
    return { params: null, error: 'apiFinishUrl inválida.' };
  }

  try {
    const ret = new URL(returnUrl);
    if (!isAllowedTenantReturnHost(ret.hostname)) {
      return { params: null, error: `returnUrl não permitida (${ret.hostname}).` };
    }
  } catch {
    return { params: null, error: 'returnUrl inválida.' };
  }

  return { params: { metaUrl, apiFinishUrl, state, returnUrl, tenant } };
}

function previewData(raw: unknown): string {
  try {
    if (typeof raw === 'string') return raw.slice(0, 200);
    return JSON.stringify(raw).slice(0, 200);
  } catch {
    return String(raw).slice(0, 200);
  }
}

function checkCoop(): string {
  try {
    if (typeof window === 'undefined') return 'unknown';
    if ('crossOriginIsolated' in window && (window as any).crossOriginIsolated) {
      return 'crossOriginIsolated=true (COOP/COEP restritivo — popups quebram!)';
    }
    return 'opener-compatible';
  } catch {
    return 'unknown';
  }
}

/**
 * Página pública (montada normalmente em app.pooh.pro/embedded-signup-launcher).
 *
 * Abre o popup do Embedded Signup da Meta, escuta o postMessage WA_EMBEDDED_SIGNUP
 * e despacha para o /finish-relay do API do tenant. Centralizar este passo num único
 * domínio permite reusar Allowed Domains da Meta para todos os tenants.
 *
 * Esta página inclui diagnóstico ao vivo: mostra todos os postMessages recebidos
 * (independentemente da origem) e estado do popup, para identificar rapidamente
 * problemas com Allowed Domains, COOP, Service Worker antigo, etc.
 */
const EmbeddedSignupLauncher: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>('preparing');
  const [message, setMessage] = useState('Preparando conexão com a Meta...');
  const [debug, setDebug] = useState<DebugEntry[]>([]);
  const [popupClosed, setPopupClosed] = useState<boolean>(false);
  const [coopHint] = useState<string>(checkCoop());

  const popupRef = useRef<Window | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const launchedRef = useRef(false);

  const parsed = useMemo(() => readLauncherParams(searchParams), [searchParams]);

  const pushDebug = (entry: Omit<DebugEntry, 'ts'>): void => {
    setDebug((prev) => {
      const next = [{ ts: new Date().toISOString().slice(11, 19), ...entry }, ...prev];
      return next.slice(0, 30);
    });
  };

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (launchedRef.current) return;
    launchedRef.current = true;

    if (!parsed.params) {
      setStatus('error');
      setMessage(parsed.error || 'Parâmetros inválidos.');
      return;
    }

    const { metaUrl, apiFinishUrl, state, returnUrl } = parsed.params;

    console.info('[EmbeddedSignupLauncher] inicializando', {
      apiFinishUrl,
      returnUrl,
      origin: window.location.origin,
      coopHint,
    });

    const features = 'popup=yes,width=720,height=900,left=60,top=40,menubar=no,toolbar=no,status=no';
    const popup = window.open(metaUrl, POPUP_NAME, features);
    if (!popup) {
      setStatus('error');
      setMessage('Popup bloqueado. Permita pop-ups para este site e tente novamente.');
      return;
    }
    popupRef.current = popup;
    setStatus('waiting');
    setMessage('Conclua o Embedded Signup na janela da Meta...');

    let openerNullSignal: string | null = null;
    try {
      // Acessar popup.opener pode disparar COOP/security; o try-catch detecta isso.
      if (popup.opener === null) {
        openerNullSignal =
          'popup.opener é null — COOP "same-origin" no app.pooh.pro provavelmente bloqueia o postMessage. Ajuste para "same-origin-allow-popups" ou remova o header.';
      }
    } catch (e) {
      openerNullSignal = `Acesso a popup.opener bloqueado (${(e as Error).message}). Sintoma de COOP restritivo.`;
    }
    if (openerNullSignal) {
      pushDebug({ origin: '(self)', fromMeta: false, isWaSignup: false, preview: openerNullSignal });
    }

    const popupPoll = window.setInterval(() => {
      if (popup.closed) {
        setPopupClosed(true);
        window.clearInterval(popupPoll);
      }
    }, POPUP_CLOSED_POLL_MS);

    const finishWithError = (err: Error): void => {
      try {
        popup.close();
      } catch {
        /* ignore */
      }
      window.clearInterval(popupPoll);
      setStatus('error');
      setMessage(err.message);
    };

    const ttl = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      cleanupRef.current = null;
      finishWithError(
        new Error(
          'Tempo esgotado aguardando a Meta. Verifique:\n' +
            '1) Allowed Domains na app Meta inclui ' +
            window.location.origin +
            '\n2) Headers da página NÃO mandam Cross-Origin-Opener-Policy: same-origin\n' +
            '3) O Service Worker (sw.js) não está servindo bundle antigo (limpe cache).'
        )
      );
    }, MESSAGE_WAIT_MS);

    const onMessage = (ev: MessageEvent): void => {
      const fromMeta = isMetaEmbeddedSignupMessageOrigin(ev.origin);
      const raw = ev.data;
      const isWaSignup =
        (typeof raw === 'object' && raw !== null && (raw as Record<string, unknown>).type === 'WA_EMBEDDED_SIGNUP') ||
        (typeof raw === 'string' && raw.includes('WA_EMBEDDED_SIGNUP'));

      pushDebug({ origin: ev.origin || '(empty)', fromMeta, isWaSignup, preview: previewData(raw) });

      if (!fromMeta) return;

      const payload = parseWaEmbeddedSignupFinishPayload(raw);
      if (!payload) {
        if (isWaSignup) {
          console.warn('[EmbeddedSignupLauncher] WA_EMBEDDED_SIGNUP não interpretado:', raw);
        }
        return;
      }

      window.clearTimeout(ttl);
      window.clearInterval(popupPoll);
      window.removeEventListener('message', onMessage);
      cleanupRef.current = null;

      setStatus('finishing');
      setMessage('Finalizando configuração no servidor do cliente...');

      void (async () => {
        try {
          console.info('[EmbeddedSignupLauncher] WA_EMBEDDED_SIGNUP FINISH:', payload);
          const res = await fetch(apiFinishUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              state,
              waba_id: payload.waba_id,
              phone_number_id: payload.phone_number_id,
              business_id: payload.business_id,
            }),
          });
          const body = (await res.json().catch(() => ({}))) as {
            success?: boolean;
            error?: string;
            displayPhoneNumber?: string;
          };

          if (!res.ok || !body.success) {
            throw new Error(body.error || `Erro HTTP ${res.status} ao chamar /finish-relay.`);
          }

          try {
            popup.close();
          } catch {
            /* ignore */
          }

          setStatus('success');
          setMessage(
            body.displayPhoneNumber
              ? `WhatsApp conectado: ${body.displayPhoneNumber}. Redirecionando...`
              : 'WhatsApp conectado com sucesso. Redirecionando...'
          );

          const sep = returnUrl.includes('?') ? '&' : '?';
          window.setTimeout(() => {
            window.location.href = `${returnUrl}${sep}embedded_signup=1`;
          }, 1200);
        } catch (e: unknown) {
          finishWithError(e instanceof Error ? e : new Error(String(e)));
        }
      })();
    };

    window.addEventListener('message', onMessage);
    cleanupRef.current = () => {
      window.clearTimeout(ttl);
      window.clearInterval(popupPoll);
      window.removeEventListener('message', onMessage);
    };
  }, [parsed, coopHint]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        p: 3,
        textAlign: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ mt: 6, maxWidth: 720, width: '100%' }}>
        {(status === 'preparing' || status === 'waiting' || status === 'finishing') && (
          <>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {status === 'finishing' ? 'Finalizando...' : 'Conectando WhatsApp...'}
            </Typography>
            <Typography color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
              {message}
            </Typography>
          </>
        )}
        {status === 'success' && (
          <>
            <Typography variant="h6" gutterBottom color="success.main">
              Conectado
            </Typography>
            <Typography color="text.secondary">{message}</Typography>
          </>
        )}
        {status === 'error' && (
          <>
            <Typography variant="h6" gutterBottom color="error">
              Não foi possível conectar
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
              {message}
            </Typography>
            <Button variant="contained" onClick={() => window.close()}>
              Fechar
            </Button>
          </>
        )}

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'left' }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
            <Chip size="small" label={`origin: ${typeof window !== 'undefined' ? window.location.origin : ''}`} />
            <Chip size="small" label={`coop: ${coopHint}`} color={coopHint.startsWith('cross') ? 'error' : 'default'} />
            <Chip size="small" label={`popup.closed: ${popupClosed}`} color={popupClosed ? 'warning' : 'default'} />
            <Chip size="small" label={`tenant: ${parsed.params?.tenant || '(?)'}`} />
          </Stack>

          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
            Diagnóstico ao vivo de postMessages:
          </Typography>
          <Box
            sx={{
              mt: 1,
              maxHeight: 220,
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: 12,
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
              p: 1,
            }}
          >
            {debug.length === 0 && (
              <Typography variant="caption" color="text.disabled">
                Nenhum postMessage recebido ainda...
              </Typography>
            )}
            {debug.map((d, i) => (
              <Box key={i} sx={{ mb: 0.5 }}>
                <span style={{ color: d.fromMeta ? (d.isWaSignup ? 'green' : '#555') : '#a00' }}>
                  [{d.ts}] {d.fromMeta ? 'META' : 'OTHER'} {d.isWaSignup ? 'WA✓' : ''} {d.origin}
                </span>
                <br />
                <span style={{ color: '#666' }}>{d.preview}</span>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EmbeddedSignupLauncher;
